import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TableSortLabel from "@mui/material/TableSortLabel";
import { ArrowDownward } from "@mui/icons-material";
import { BackButton } from "@egovernments/digit-ui-react-components";

const ReusableTable = ({
  columns,
  data,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultRowsPerPage = 25,
  tableTitle = "",
  backButton = true,
  backTo = -1,
  isLTPGrading = false,
}) => {
  const [orderBy, setOrderBy] = useState(columns[0]?.id || "");
  const [order, setOrder] = useState("asc");
  const [globalSearch, setGlobalSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // Conditional filtering: Only filter owner rows if it's LTP Grading
  const filteredData = isLTPGrading
    ? data.filter((row) => {
        const values = Object.values(row).map((v) => (v ? v.toString().toLowerCase().trim() : ""));
        return !values.some((val) => val === "owner" || val.includes("owner"));
      })
    : data;

  // Conditional table title: Use specific tittle for LTP Grading or otherwise use prop
  const displayTableTitle = isLTPGrading ? "LTP Grading Dashboard" : tableTitle;

  // Sorting
  const getComparator = (order, orderBy) => (a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    if (order === "desc") return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
    else return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
  };

  // Searching - use filtered data as base
  const searchedData = filteredData.filter((row) =>
    columns.some((col) => row[col.id]?.toString().toLowerCase().includes(globalSearch.toLowerCase()))
  );

  // Sorting
  const sortedData = searchedData.sort(getComparator(order, orderBy));

  // Pagination
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="reusable-table-container">
      {/* Header Section */}
      <div className="reusable-table-header">{backButton && <BackButton />}</div>

      {/* Title and Search Section */}
      <div className="reusable-table-title-section">
        {displayTableTitle && <h2 className="reusable-table-title">{displayTableTitle}</h2>}
        <div className="reusable-table-search-container">
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search here..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className="reusable-table-search-icon" />
                </InputAdornment>
              ),
              endAdornment: globalSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setGlobalSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            className="reusable-table-search-field"
          />
        </div>
      </div>

      {/* Table Container */}
      <div className="reusable-table-main-container">
        <TableContainer className="reusable-table-table-container">
          <Table stickyHeader className="reusable-table-table">
            <thead>
              <tr>
                {columns.map((col, index) => (
                  <th key={col.id} className="reusable-table-header-cell">
                    <div className="reusable-table-header-content">
                      <span>{col.label}</span>
                      {col.sortable && (
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : "asc"}
                          onClick={() => handleSort(col.id)}
                          sx={{
                            color: "white !important",
                            "& .MuiTableSortLabel-icon": {
                              opacity: "1 !important",
                              color: "white !important",
                            },
                            "&:hover": {
                              color: "white !important",
                            },
                          }}
                          IconComponent={(props) => (
                            <ArrowDownward
                              {...props}
                              sx={{
                                color: "white !important",
                                opacity: 1,
                                fontSize: "16px",
                              }}
                            />
                          )}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="reusable-table-no-records">
                    No records found
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr key={row.id || idx} className={idx % 2 === 0 ? "reusable-table-even-row" : "reusable-table-odd-row"}>
                    {columns.map((col) => (
                      <td key={col.id} className="reusable-table-body-cell">
                        {col.render ? col.render(row, idx) : row[col.id]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          className="reusable-table-pagination"
          component="div"
          count={searchedData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </div>
    </div>
  );
};

export default ReusableTable;
